// SCR-312: one proposed change under review (REQ-112, REQ-113, ADR-062). The before/after table
// by path (the card shows it), each risk flag with its explanation and a box for the phrase it
// asks for (shown as a hint; never prefilled), then Approve (enabled only once every phrase
// matches) and Reject (with a reason the Assistant reads). Approve posts the digest as shown and the phrases
// as typed; a refusal is explained in words with what to do. Every server string is shown with
// any id masked (AS-1).
import type { JSX } from "preact";
import { useId, useState } from "preact/hooks";
import { ApiError } from "../../api/errors";
import { Field, errorId, hintId } from "../../primitives/field";
import {
  allConfirmed,
  approvalProblem,
  approveRequest,
  APPROVAL_HELP,
  diffValue,
  FLAG_LABELS,
  isTransient,
  MAX_REJECT_REASON,
  phraseHint,
  phraseMatches,
  REASON_REFUSED,
  rejectReasonProblem,
  rejectReasonText,
  TRANSIENT_PROBLEM,
  type AgentChange,
  type AgentDiffEntry,
  type AgentRiskFlag,
  type ApprovalProblem,
  type TypedPhrases,
} from "./confirmations";
import { withoutIds } from "./model";
import { useAssistantTransport } from "./transport";

function asApiError(error: unknown): ApiError {
  return error instanceof ApiError ? error : new ApiError(0, "internal", "Something went wrong.");
}

/** The before/after table by path (`now` labels the after column once applied). */
export function DiffTable({ diff, applied = false }: { diff: readonly AgentDiffEntry[]; applied?: boolean }) {
  if (diff.length === 0) return <p class="muted">No field changes.</p>;
  return (
    <div class="table-scroll">
      <table class="data-table assistant-diff">
        <caption class="visually-hidden">{applied ? "What changed" : "What would change"}</caption>
        <thead>
          <tr>
            <th scope="col">Field</th>
            <th scope="col">{applied ? "Was" : "Now"}</th>
            <th scope="col">{applied ? "Now" : "After"}</th>
          </tr>
        </thead>
        <tbody>
          {diff.map((entry, i) => (
            <tr key={i}>
              <th scope="row">{withoutIds(entry.path)}</th>
              <td>{diffValue(entry.before)}</td>
              <td>{diffValue(entry.after)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** A refusal or failure in words, with what to do. */
export function ProblemBanner({ problem, action }: { problem: ApprovalProblem; action?: JSX.Element | undefined }) {
  return (
    <div class="banner banner-warning assistant-change-problem" role="alert">
      <p>
        <strong>{problem.title}.</strong> {problem.text}
      </p>
      {action}
    </div>
  );
}

function FlagConfirmation({ flag, value, onInput, disabled }: { flag: AgentRiskFlag; value: string; onInput(value: string): void; disabled: boolean }) {
  const id = useId();
  const typed = value.trim() !== "";
  const wrong = typed && !phraseMatches(flag, value);
  return (
    <li class="assistant-flag">
      <p class="assistant-flag-title">
        <strong>{FLAG_LABELS[flag.name]}</strong>
      </p>
      <p class="assistant-flag-why">{withoutIds(flag.explanation)}</p>
      <Field
        id={id}
        label={`Confirm: ${FLAG_LABELS[flag.name]}`}
        help={APPROVAL_HELP.confirmation}
        hideLabel
        hint={
          <>
            {phraseHint(flag)} <strong class="assistant-phrase">{withoutIds(flag.expected)}</strong>
          </>
        }
        error={wrong ? "That does not match yet." : null}
      >
        <input
          id={id}
          type="text"
          class="input"
          autocomplete="off"
          spellcheck={false}
          value={value}
          disabled={disabled}
          aria-describedby={wrong ? `${hintId(id)} ${errorId(id)}` : hintId(id)}
          onInput={(event) => onInput(event.currentTarget.value)}
        />
      </Field>
    </li>
  );
}

export interface ChangeReviewProps {
  sessionId: string;
  change: AgentChange;
  /** The change as the API answered an approve, a reject or a re-read. */
  onChanged(change: AgentChange): void;
}

/** Approve and Reject for a proposed change; the caller shows it only while `proposed`. */
export function ChangeReview({ sessionId, change, onChanged }: ChangeReviewProps) {
  const transport = useAssistantTransport();
  const [typed, setTyped] = useState<TypedPhrases>({});
  const [busy, setBusy] = useState<"approving" | "rejecting" | null>(null);
  const [problem, setProblem] = useState<{ problem: ApprovalProblem; code: string; action: "approve" | "reject" | "reread" } | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const reasonId = useId();

  const confirmed = allConfirmed(change.flags, typed);
  const reasonProblem = rejectReasonProblem(reason);

  /** A refusal in words: a transient answer is not a decision, and a refused reason is the reason's fault. */
  const explain = (err: unknown, action: "approve" | "reject" | "reread"): ApprovalProblem => {
    const error = asApiError(err);
    const found = isTransient(error.status)
      ? TRANSIENT_PROBLEM
      : action === "reject" && error.code === "validation_failed"
        ? REASON_REFUSED
        : approvalProblem(error.code);
    setProblem({ problem: found, code: error.code, action });
    return found;
  };

  const reread = () =>
    transport
      .change(sessionId, change.id)
      .then((fresh) => {
        setProblem(null);
        setTyped({});
        onChanged(fresh);
      })
      .catch((err: unknown) => explain(err, "reread"));

  // After any refusal but artifact_changed, confirmation_required and a refused reason, the change
  // is read again so the card shows what the server recorded (a route's refusal ends it `failed`,
  // and a 5xx may have applied it); a re-read that fails leaves the card and its banner as they are.
  const refused = (action: "approve" | "reject") => (err: unknown) => {
    const found = explain(err, action);
    if (found.reload) void transport.change(sessionId, change.id).then(onChanged).catch(() => undefined);
  };

  const approve = () => {
    if (!confirmed || busy) return;
    setBusy("approving");
    setProblem(null);
    transport
      .approve(sessionId, change.id, approveRequest(change, typed))
      .then(onChanged)
      .catch(refused("approve"))
      .finally(() => setBusy(null));
  };

  const sendReject = () => {
    if (reasonProblem !== null || busy) return;
    setBusy("rejecting");
    setProblem(null);
    transport
      .reject(sessionId, change.id, rejectReasonText(reason))
      .then(onChanged)
      .catch(refused("reject"))
      .finally(() => setBusy(null));
  };

  const reject = (event: JSX.TargetedEvent<HTMLFormElement>) => {
    event.preventDefault();
    sendReject();
  };

  const retry = () => {
    if (problem?.action === "approve") approve();
    else if (problem?.action === "reject") sendReject();
    else void reread();
  };

  return (
    <div class="assistant-change-review">
      {change.flags.length > 0 && (
        <section class="assistant-flags" aria-label="Risks to confirm">
          <p>This change is risky. Type each confirmation exactly as shown before you approve.</p>
          <ul class="assistant-flag-list">
            {change.flags.map((flag) => (
              <FlagConfirmation
                key={flag.name}
                flag={flag}
                value={typed[flag.name] ?? ""}
                disabled={busy !== null}
                onInput={(value) => setTyped((t) => ({ ...t, [flag.name]: value }))}
              />
            ))}
          </ul>
        </section>
      )}
      {problem && (
        <ProblemBanner
          problem={problem.problem}
          action={
            problem.problem.retryable ? (
              <button type="button" class="button button-secondary" disabled={busy !== null} onClick={retry}>
                Try again
              </button>
            ) : problem.code === "artifact_changed" ? (
              <button type="button" class="button button-secondary" onClick={() => void reread()}>
                Reload the change
              </button>
            ) : undefined
          }
        />
      )}
      {busy === "approving" && (
        <p class="assistant-applying" role="status">
          <span class="spinner" aria-hidden="true" /> Applying the change…
        </p>
      )}
      <div class="assistant-change-actions">
        <button type="button" class="button button-primary" disabled={!confirmed || busy !== null} onClick={approve}>
          Approve
        </button>
        {!rejecting && (
          <button type="button" class="button button-secondary" disabled={busy !== null} onClick={() => setRejecting(true)}>
            Reject
          </button>
        )}
      </div>
      {rejecting && (
        <form class="assistant-reject" aria-label="Reject this change" onSubmit={reject}>
          <Field
            id={reasonId}
            label="Why reject it?"
            help={APPROVAL_HELP.rejectReason}
            hint="The Assistant reads this and can propose something else."
            error={reason.trim() !== "" && reasonProblem !== null ? reasonProblem : null}
          >
            <textarea
              id={reasonId}
              class="input"
              rows={2}
              maxLength={MAX_REJECT_REASON + 50}
              value={reason}
              disabled={busy !== null}
              aria-describedby={hintId(reasonId)}
              onInput={(event) => setReason(event.currentTarget.value)}
            />
          </Field>
          <div class="assistant-change-actions">
            <button type="submit" class="button button-danger" disabled={reasonProblem !== null || busy !== null}>
              Reject change
            </button>
            <button type="button" class="button button-secondary" disabled={busy !== null} onClick={() => setRejecting(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
